import { authService } from '../services/auth/authService';

const LOCK_NAME = 'zivo_portal_auth_refresh';

const ACCESS_SYNC = 'access_token_sync';

type AccessTokenSyncPayload = {
  type: typeof ACCESS_SYNC;
  access_token: string;
  csrf_token?: string;
};

export function isAccessTokenSyncMessage(data: unknown): data is AccessTokenSyncPayload {
  if (!data || typeof data !== 'object') return false;
  const o = data as Record<string, unknown>;
  return o.type === ACCESS_SYNC && typeof o.access_token === 'string';
}

/**
 * One tab refreshes the session; others skip and rely on {@link broadcastAccessTokenSync}
 * (or the same tab applies tokens from refresh).
 */
export async function coordinatedRefreshAccessToken(): Promise<'done' | 'skipped'> {
  const run = async (): Promise<'done'> => {
    await authService.refreshAccessToken();
    broadcastAccessTokenSync();
    return 'done';
  };

  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) {
    return run();
  }

  try {
    const result = await locks.request(
      LOCK_NAME,
      { ifAvailable: true },
      async (lock: Lock | null) => {
        if (lock == null) {
          return 'skipped' as const;
        }
        await authService.refreshAccessToken();
        broadcastAccessTokenSync();
        return 'done' as const;
      }
    );
    return result === 'skipped' ? 'skipped' : 'done';
  } catch {
    await authService.refreshAccessToken();
    broadcastAccessTokenSync();
    return 'done';
  }
}

/** Notify other tabs to apply the in-memory tokens from the leader tab (same-origin). */
export function broadcastAccessTokenSync(): void {
  const token = authService.getToken();
  if (!token) return;
  const csrf = authService.getCsrfToken();
  const ch = new BroadcastChannel('auth_sync');
  const payload: AccessTokenSyncPayload = {
    type: ACCESS_SYNC,
    access_token: token,
    csrf_token: csrf ?? undefined,
  };
  ch.postMessage(payload);
  ch.close();
}
